import React from 'react';
import { Box, FormControlLabel, Slider, Switch, Typography } from '@mui/material';
import { ReelGlow, ReelShadow } from '../reelCanvas.types';
import { ColorField } from './ColorField';

export const LabeledSlider: React.FC<{
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    unit?: string;
    onChange: (v: number) => void;
    disabled?: boolean;
}> = ({ label, value, min, max, step = 1, unit = '', onChange, disabled }) => (
    <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {value}
                {unit}
            </Typography>
        </Box>
        <Slider size="small" min={min} max={max} step={step} value={value} disabled={disabled} onChange={(_e, v) => onChange(v as number)} aria-label={label} />
    </Box>
);

/** Eight compass directions + free angle for shadows. */
const DIRECTIONS: ReadonlyArray<{ label: string; angle: number }> = [
    { label: '↖', angle: 225 },
    { label: '↑', angle: 270 },
    { label: '↗', angle: 315 },
    { label: '←', angle: 180 },
    { label: '•', angle: -1 },
    { label: '→', angle: 0 },
    { label: '↙', angle: 135 },
    { label: '↓', angle: 90 },
    { label: '↘', angle: 45 },
];

export const ShadowFields: React.FC<{
    title: string;
    value: ReelShadow;
    onChange: (v: ReelShadow) => void;
}> = ({ title, value, onChange }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControlLabel
            control={<Switch size="small" color="secondary" checked={value.enabled} onChange={(e) => onChange({ ...value, enabled: e.target.checked })} />}
            label={<Typography variant="subtitle2">{title}</Typography>}
        />
        {value.enabled && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, pl: 1 }}>
                <ColorField label="Shadow colour" value={value.color} onChange={(c) => onChange({ ...value, color: c })} />
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Direction ({value.angle}°)
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 28px)', gap: 0.25, mt: 0.5 }}>
                        {DIRECTIONS.map((d) => (
                            <Box
                                key={d.label}
                                role="button"
                                aria-label={d.angle < 0 ? 'Centre (no offset)' : `Direction ${d.angle} degrees`}
                                onClick={() => onChange(d.angle < 0 ? { ...value, distance: 0 } : { ...value, angle: d.angle, distance: value.distance || 6 })}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    border: '1px solid',
                                    borderColor:
                                        (d.angle < 0 && value.distance === 0) || (d.angle >= 0 && value.angle === d.angle && value.distance > 0)
                                            ? 'secondary.main'
                                            : 'divider',
                                    backgroundColor:
                                        (d.angle < 0 && value.distance === 0) || (d.angle >= 0 && value.angle === d.angle && value.distance > 0)
                                            ? 'action.selected'
                                            : 'transparent',
                                }}
                            >
                                {d.label}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <LabeledSlider label="Angle" value={value.angle} min={0} max={360} unit="°" onChange={(v) => onChange({ ...value, angle: v })} />
                <LabeledSlider label="Distance" value={value.distance} min={0} max={80} unit="px" onChange={(v) => onChange({ ...value, distance: v })} />
                <LabeledSlider label="Blur" value={value.blur} min={0} max={80} unit="px" onChange={(v) => onChange({ ...value, blur: v })} />
            </Box>
        )}
    </Box>
);

export const GlowFields: React.FC<{
    title: string;
    value: ReelGlow;
    onChange: (v: ReelGlow) => void;
}> = ({ title, value, onChange }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControlLabel
            control={<Switch size="small" color="secondary" checked={value.enabled} onChange={(e) => onChange({ ...value, enabled: e.target.checked })} />}
            label={<Typography variant="subtitle2">{title}</Typography>}
        />
        {value.enabled && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, pl: 1 }}>
                <ColorField label="Glow colour" value={value.color} onChange={(c) => onChange({ ...value, color: c })} />
                <LabeledSlider label="Glow size" value={value.size} min={0} max={120} unit="px" onChange={(v) => onChange({ ...value, size: v })} />
            </Box>
        )}
    </Box>
);
